
<template>
  <div class="flex flex-col items-center justify-center">
    <div class="flex justify-center py-3 logo-container w-full">
      <img
        :src="require('../../../../../assets/images/fcLOgo.svg')"
        alt="Fresh Clinics"
        width="251"
        height="56"
      />
    </div>
    <div class="w-11/12 mx-auto my-8 px-8 pt-8 pb-16 space-y-10 rounded-lg bg-login mfa-container">
      <vs-alert class="h-auto" color="success" icon="check" v-if="resentSuccessful">
        <span class="font-semibold">Your OTP was resent successfully</span>
      </vs-alert>
      <div class="text-left justify-between flex items-center">
        <p class="form-heading">{{signingIn ?  ' OTP has been successfully verified' : 'Verify Your Identity'}}</p>
        <span v-if="!signingIn && resendCountdown < 5" class="text-xs cursor-pointer underline text-primary" @click="handleSignIn">Go back to login</span>
      </div>
    
      <p class=" text-gray-600" v-if="!signingIn">
        Please enter the 6-digit code we sent to your {{ mfaMethod == 'sms' ? 'mobile number' : 'email address' }}
      </p>
      <p class="text-gray-600" v-else>
        Please wait while we redirect you to your dashboard.
      </p>
      <template>
        <div class="flex items-center justify-center bg-gray-50">
          <div class="w-full max-w-md rounded-2xl">
            <form class="" v-if="!signingIn">
              <div class="flex justify-between gap-2 otp-wrapper" :class="{ 'otp-error': hasOtpError }">
                <input
                  v-for="(digit, index) in otp"
                  :key="index"
                  ref="otpInputs"
                  v-model="otp[index]"
                  type="text"
                  inputmode="numeric"
                  maxlength="1"
                  :class="`otp-input ${hasOtpError ? 'error' : ''}`"
                  @input="onInput(index)"
                  @keydown.backspace="onBackspace(index, $event)"
                  @paste="handlePaste($event)"
                  @keyup.enter="verifyMFA"
                />
              </div>
            </form>
            <div class="flex items-center justify-center gap-3 text-gray-600" v-else>
              <span class="otp-spinner"></span>
              <p class="text-2xl font-bold text-center">Signing In...</p>
            </div>
          </div>
        </div>
        <div>
          <p v-if="showErrorMessage" class="text-sm text-red-700 py-4">{{ errorMessage }}</p>
          <vs-button
            class="w-full"
            form="login-form"
            :disabled="isVerifying || !isOtpComplete || isSending || isUsingEmailInstead || signingIn"
            @click="verifyMFA"
            >{{ isVerifying ? 'Verifying your code...' : 'Verify' }}</vs-button
          >
        </div>
        <div>
          <p class="text-sm text-gray-500 text-center py-4" v-if="resendDisabled && !resendCountdown">Sending OTP via {{ mfaMethod }}...</p>
          <p v-else-if="resendDisabled && resendCountdown" class="text-sm text-gray-500 text-center py-4">
            You can resend a new OTP after {{ resendCountdown }} seconds.
          </p>
          <p v-else-if="sessionExpired" class="text-sm text-gray-500 text-center py-4">
            Session expired. Redirecting you to the login page <br/> in 5 seconds.
          </p>
          <template v-else>
            <div class="text-center">
              <span class="text-sm text-gray-700">
                Didn’t receive the code?
              </span>
              <vs-button :disabled="isVerifying || isSending || isUsingEmailInstead || resendDisabled || signingIn" color="primary" class="mfa-resend-btn font-semibold" type="flat"
                @click="requestNewOtp(switchMfaMethod = false)"
              >{{ isSending ? 'Resending...' : 'Resend' }}</vs-button>
            </div>
            <div class="text-center mt-4">
              <span class="text-base mr-0">Or</span>
              <vs-button :disabled="isVerifying || isSending || isUsingEmailInstead || resendDisabled || signingIn" color="primary" class="mfa-resend-btn font-semibold" type="flat"
                @click="requestNewOtp(switchMfaMethod = true)"
              >
                <span v-if="mfaMethod == 'sms'" class="underline">{{ isUsingEmailInstead ? 'Sending code via email...' : 'use email instead' }}</span>
                <span v-else class="underline">{{ isUsingEmailInstead ? 'Sending code via sms...' : 'use sms instead' }}</span>
              </vs-button>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
const METHODS = { SMS: 'sms', EMAIL: 'email' };
import loginSuccessHandler from '@/utils/loginSuccessHandler';
import { getDeviceFingerprint, getDeviceName } from '@/utils/mfa';

export default {
  data() {
    return {
      otp: ['', '', '', '', '', ''],
      errorMessage: 'We couldn’t verify that code. Please check it and try again.',
      showErrorMessage:false,
      isVerifying:false,
      isSending:false,
      isUsingEmailInstead:false,
      mfaMethod: METHODS.SMS,
      mfaToken:'',
      sessionToken:'',
      mfaResumePayload:{},
      hasOtpError:false,
      resendDisabled: false,
      resendCountdown: 0, 
      resendInterval: null,
      resentSuccessful:false,
      sessionExpired:false,
      deviceFingerprint: '',
      deviceName: '',
      signingIn: false,
    };
  },
  async mounted() {
    this.deviceFingerprint = await getDeviceFingerprint();
    this.deviceName = getDeviceName();

    this.$vs.loading.close();
    this.checkResendCooldown();

    const mfaSession = this.getMfaSession();
    if (!mfaSession) return;

    Object.assign(this, mfaSession);
  },
  methods: {
    handleSignIn() {
      this.$router.push('/login');
      sessionStorage.removeItem('mfaSession');
      sessionStorage.removeItem('lastOtpSentAt');
    },
    checkResendCooldown() {
      const lastSent = sessionStorage.getItem('lastOtpSentAt');
      if (!lastSent) return;

      const elapsed = Date.now() - parseInt(lastSent, 10);
      const cooldown = 60000; // 60 seconds

      if (elapsed < cooldown) {
        this.resendDisabled = true;
        this.resendCountdown = Math.ceil((cooldown - elapsed) / 1000);

        this.startCountdown();
      }
    },
    startCountdown() {
      clearInterval(this.resendInterval);
      this.resendInterval = setInterval(() => {
        if (this.resendCountdown > 1) {
          this.resendCountdown--;
        } else {
          this.resendDisabled = false;
          this.resendCountdown = 0;
          clearInterval(this.resendInterval);
        }
      }, 1000);
    },
    getMfaSession() {
      const raw = sessionStorage.getItem('mfaSession');
      return raw ? JSON.parse(raw) : null;
    },
    onInput(index) {
      this.otp[index] = this.otp[index].replace(/\D/g, '');

      if (this.otp[index] && index < this.otp.length - 1) {
        this.$refs.otpInputs[index + 1].focus();
      }
    },
    handlePaste(event) {
      this.resetFlags();
      
      event.preventDefault();
      const pasteData = event.clipboardData.getData('text').trim();
      if (/^\d{6}$/.test(pasteData)) {
        this.otp = pasteData.split('');
        this.$refs.inputs[this.otp.length - 1].focus();
      }
    },
    onBackspace(index) {
      if (!this.otp[index] && index > 0) {
        this.$refs.otpInputs[index - 1].focus();
      }
    },
    async requestNewOtp(switchMfaMethod = false) {
      if (this.resendDisabled || this.resendCountdown > 0) return;

      if (switchMfaMethod) {
        this.mfaMethod = this.mfaMethod === METHODS.SMS ? METHODS.EMAIL : METHODS.SMS;
      }

      this.resendDisabled = true;

      try {
        await this.$store.dispatch('auth/requestNewMFA', {
          sessionToken: this.sessionToken,
          mfaMethod: this.mfaMethod,
        });

        sessionStorage.setItem('lastOtpSentAt', Date.now().toString());
        this.resendCountdown = 60;
        this.checkResendCooldown();
        this.resetFlags();
        this.resentSuccessful = true;
      } catch (error) {
        this.showErrorMessage = true;
        this.errorMessage = error.data.message;
        this.hasOtpError = false;
        this.resendDisabled = false;
        this.resentSuccessful = false;

        if (switchMfaMethod) { // reset mfaMethod if failed
          this.mfaMethod = this.mfaMethod === METHODS.SMS ? METHODS.EMAIL : METHODS.SMS;
        }

        if (error.status === 419) this.handleExpiredSession();
      } finally {
        this.otp = ['', '', '', '', '', ''];
      }
    },
    handleExpiredSession() {
      sessionStorage.removeItem('mfaSession');
      sessionStorage.removeItem('lastOtpSentAt');

      this.sessionExpired = true;

      setTimeout(() => {
        this.$router.push('/login');
      }, 5000);
    },
    async verifyMFA() {
      if (!this.isOtpComplete || this.isVerifying) return;

      this.isVerifying = true;
      this.showErrorMessage = false;
      this.hasOtpError = false;

      try {
        const payload = {
          otp: this.otp.join(''),
          deviceFingerprint: this.deviceFingerprint,
          deviceName: this.deviceName,
          sessionToken: this.sessionToken,
        };

        const { data } = await this.$store.dispatch('auth/verifyMFA', payload);
        const tempPayload = this.mfaResumePayload;

        this.signingIn = true;
        this.isVerifying = false;

        sessionStorage.removeItem('mfaSession');
        sessionStorage.removeItem('lastOtpSentAt');

        await loginSuccessHandler({
          response: {
            data: {
              data: {
                user: data.data.userDetails.user, 
                token: data.data.userDetails.token,
              }},
          },
          payload: tempPayload,
          commit: this.$store.commit.bind(this.$store),
          router: this.$router,
        });
      } catch (error) {
        this.errorMessage = error.data.message || 'We couldn’t verify that code. Please try again.';
        this.showErrorMessage = true;
        this.hasOtpError = true;
        this.resentSuccessful = false;

        if (error.status === 419) this.handleExpiredSession();
      } finally {
        this.isVerifying = false;
      }
    },
    resetFlags(){
      this.hasOtpError = false;
      this.showErrorMessage = false;
      this.errorMessage = '';
    },
  },
  computed: {
    isOtpComplete() {
      return this.otp.every(digit => digit !== '');
    },
  },
};
</script>

<style lang="scss">
.mfa-container {
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.1);
  max-width: 32rem;

  .con-tab {
    padding-bottom: 14px;
  }
  .con-slot-tabs {
    margin-top: 1rem;
  }
}

.form-heading {
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #0e4333;
}

.logo-container {
  border-bottom: 2px solid #ebe9f1;
}

.otp-input {
  width: 48px;
  height: 48px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;

  border: 1px solid #d1d5db; /* gray-300 */
  border-radius: 8px;
  background-color: #ffffff;

  outline: none;
  appearance: none;
  -webkit-appearance: none;

  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.otp-input:focus {
  border-color: #3b82f6; /* blue-500 */
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.35);
}

/* Remove number input spinners (if changed to number accidentally) */
.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Disabled state */
.otp-input:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Error state (optional) */
.otp-input.error {
  border-color: #c53030;
}

.otp-input.error:focus {
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.35);
}

.mfa-resend-btn {
  padding: 0 5px !important;

  &:hover {
    background-color: transparent !important;
    opacity: 0.7;
  }
}

.otp-wrapper {
  display: flex;
  gap: 0.5rem;
}

.otp-error {
  animation: shake 0.35s ease;
}

.otp-error .otp-input {
  border-color: #ef4444;
}

@keyframes shake {
  0% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
  100% { transform: translateX(0); }
}

.otp-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;       /* gray-300 */
  border-top-color: transparent;
  border-radius: 50%;
  animation: otp-spin 1s linear infinite;
}

@keyframes otp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
